/**
 * @fileoverview `<PortalState>` — états visuels du portail citoyen.
 *
 * Centralise les états empty, pending, success, error, expired et failed pour
 * éviter que chaque page réinvente ses icônes, couleurs, rôles ARIA ou textes
 * d'action. Ce composant reste frontend-only : il affiche un état transmis par
 * l'écran, sans appeler de service ni interpréter de réponse backend.
 */

import type { ReactNode } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Hourglass,
  SearchX,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Progress } from './progress.tsx';
import { cn } from '../lib/utils.ts';

/**
 * État visuel public du portail.
 *
 * - `empty` : recherche aboutie sans résultat.
 * - `pending` : traitement, retour guichet ou synchronisation en cours.
 * - `success` : action confirmée côté citoyen.
 * - `error` : erreur récupérable ou service momentanément indisponible.
 * - `expired` : session, lien ou transaction expirée.
 * - `failed` : paiement ou opération refusée.
 */
export type PortalStateStatus = 'empty' | 'pending' | 'success' | 'error' | 'expired' | 'failed';

export interface PortalStateProps {
  /** État visuel à afficher. */
  status: PortalStateStatus;
  /** Titre court et compréhensible sans jargon technique. */
  title: ReactNode;
  /** Détail optionnel, orienté prochaine action utilisateur. */
  children?: ReactNode;
  /** Action principale, généralement un `<Button>`. */
  action?: ReactNode;
  /** Action secondaire, généralement un bouton ghost ou outline. */
  secondaryAction?: ReactNode;
  /** Progression bornée uniquement pour `status="pending"`. */
  progressValue?: number;
  /** Badge métier optionnel, par exemple `<StatusBadge />`. */
  badge?: ReactNode;
  /**
   * Élément de titre rendu. Par défaut `h2` : un panneau d'état vit le plus
   * souvent SOUS le `<h1>` de la page (résultats de recherche, retour de
   * paiement…). Passer `h1` quand le panneau EST le contenu principal (ex.
   * page 404), pour garantir un `<h1>` unique (axe `page-has-heading-one`).
   */
  titleAs?: 'h1' | 'h2' | 'h3';
  className?: string;
}

interface PortalStateConfig {
  icon: LucideIcon;
  containerClassName: string;
  iconClassName: string;
  role?: 'status' | 'alert';
}

const STATE_CONFIG: Record<PortalStateStatus, PortalStateConfig> = {
  empty: {
    icon: SearchX,
    containerClassName: 'border-border bg-card',
    iconClassName: 'bg-quipux-navy-50 text-quipux-navy-700',
    role: 'status',
  },
  pending: {
    icon: Hourglass,
    containerClassName: 'border-quipux-blue-200 bg-quipux-blue-50',
    iconClassName: 'bg-white text-quipux-blue-700',
    role: 'status',
  },
  success: {
    icon: CheckCircle2,
    containerClassName: 'border-status-paid/30 bg-status-paid/5',
    iconClassName: 'bg-white text-status-paid',
    role: 'status',
  },
  error: {
    icon: AlertCircle,
    containerClassName: 'border-status-failed/30 bg-status-failed/5',
    iconClassName: 'bg-white text-status-failed',
    role: 'alert',
  },
  expired: {
    icon: Clock3,
    containerClassName: 'border-status-expired/40 bg-status-expired/10',
    iconClassName: 'bg-white text-quipux-navy-800',
    role: 'alert',
  },
  failed: {
    icon: XCircle,
    containerClassName: 'border-status-failed/30 bg-status-failed/5',
    iconClassName: 'bg-white text-status-failed',
    role: 'alert',
  },
};

/**
 * Panneau d'état du portail citoyen.
 *
 * @param props - Statut visuel, titre, contenu, actions et progression.
 * @returns Section accessible et responsive pour les états de parcours.
 *
 * @example
 * ```tsx
 * <PortalState
 *   status="empty"
 *   title="Aucune contravention trouvée"
 *   action={<Button variant="outline">Modifier la plaque</Button>}
 * >
 *   Vérifiez la plaque ou réessayez plus tard.
 * </PortalState>
 * ```
 */
export function PortalState({
  status,
  title,
  children,
  action,
  secondaryAction,
  progressValue,
  badge,
  titleAs: TitleTag = 'h2',
  className,
}: PortalStateProps): React.JSX.Element {
  const config = STATE_CONFIG[status];
  const Icon = config.icon;

  return (
    <section
      role={config.role}
      className={cn('rounded-lg border p-5', config.containerClassName, className)}
    >
      <div className="flex gap-4">
        <div
          className={cn(
            'flex size-10 shrink-0 items-center justify-center rounded-full',
            config.iconClassName,
          )}
          aria-hidden="true"
        >
          <Icon className={cn('size-5', status === 'pending' && 'animate-pulse')} />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <TitleTag className="text-quipux-navy-950 text-base font-semibold">{title}</TitleTag>
            {badge}
          </div>

          {children && <div className="text-muted-foreground text-sm leading-6">{children}</div>}

          {status === 'pending' && typeof progressValue === 'number' && (
            <Progress ariaLabel="Progression de l'etat en cours" value={progressValue} showValue />
          )}

          {(action || secondaryAction) && (
            <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap">
              {action}
              {secondaryAction}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
